/**
 * Sidebar - Fixed left navigation panel for desktop viewports.
 *
 * Renders a 240px-wide column pinned to the left edge of the viewport with:
 *	 - Brand area at the top ("GBRANDS" + "WORK ORDERS" subtitle)
 *	 - Main navigation links (Dashboard, Work Orders, Inspections, Buildings)
 *	 - Administration section (Admin, API Keys) — role-gated
 *	 - Bottom utility links (Sync Status with online/offline dot, Settings)
 *
 * Active route detection uses React Router's useLocation() hook. The active
 * item receives a left accent border, brand-primary text color, and a subtle
 * background tint. Inactive items are gray with no border.
 *
 * Visibility:
 *	 - Hidden on mobile (below tablet breakpoint)
 *	 - Displayed as a flex column on tablet+ via `hidden tablet:flex`
 *
 * The link list itself lives in the shared useNavItems() hook, which the
 * mobile MoreMenuSheet renders from as well — one definition, one set of
 * role gates, both viewports.
 *
 * This component does not accept props — it reads nav items internally via
 * useNavItems() for role-based link visibility and useOfflineSync() for the
 * online/offline indicator dot.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOfflineSync } from '../../../hooks/useOfflineSync';
import { useNavItems, isNavItemActive as isActive, type NavItem } from '../../../hooks/useNavItems';
import { BrandWordmark, BRAND_TAGLINE, PoweredByDirt } from '../../atoms';

/**
 * Props for the Sidebar component.
 *
 * @property className - Optional outer className merged onto the root element
 */
export interface SidebarProps {
	className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ className }) => {
	/* ---------------------------------------------------------------
	 * Hooks
	 * --------------------------------------------------------------- */

	/** Current route location for active-state detection */
	const location = useLocation();

	/** Programmatic navigation for client-side routing (no full reload) */
	const navigate = useNavigate();

	/** Shared, role-gated navigation definitions (also used by MoreMenuSheet) */
	const { mainNavItems, adminNavItems } = useNavItems();

	/** Offline context — provides isOnline flag for the sync status dot */
	const { isOnline } = useOfflineSync();

	/* ---------------------------------------------------------------
	 * Click handler — uses React Router for client-side navigation
	 * --------------------------------------------------------------- */

	/**
	 * Handles nav item clicks. Prevents the default anchor behavior and
	 * uses React Router's navigate() for seamless client-side transitions.
	 */
	const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
		e.preventDefault();
		navigate(href);
	};

	/* ---------------------------------------------------------------
	 * Render helper — builds a single nav item row with icon + label
	 * --------------------------------------------------------------- */

	/**
	 * Renders an individual navigation item with active/inactive styling.
	 * Active items get a 4px left border in brand-primary, brand-primary
	 * text color, and a subtle orange background tint. Inactive items
	 * show gray text with no border highlight.
	 */
	const renderNavItem = (item: NavItem) => {
		const active = isActive(item.href, location.pathname);

		return (
			<a
				key={item.href}
				href={item.href}
				onClick={(e) => handleNavClick(e, item.href)}
				className={[
					/* Base layout: horizontal flex row, centered items, full width.
					   min-h-44px because the sidebar is on screen from `tablet`
					   up — an iPad, which is still a finger. The padding alone
					   landed these rows at 41px. */
					'flex items-center gap-12px px-16px py-10px min-h-44px desktop:min-h-40px text-14px font-sans',
					/* Smooth color and background transitions */
					'transition-all duration-200',
					/* Active state: left accent border, brand-primary text, subtle bg tint */
					active
						? 'border-l-4px border-brand-primary text-brand-primary bg-brand-primary-lighter/10 font-semibold'
						/* Inactive state: transparent left border (preserves alignment), gray text */
						: 'border-l-4px border-transparent text-brand-text-muted hover:text-brand-primary hover:bg-brand-surface',
				].join(' ')}
				aria-current={active ? 'page' : undefined}
			>
				{/* Icon — inherits text color from parent for active/inactive styling */}
				<span className="flex-shrink-0">{item.icon}</span>
				{/* Label text */}
				<span>{item.label}</span>
			</a>
		);
	};

	/* ---------------------------------------------------------------
	 * Component JSX
	 * --------------------------------------------------------------- */

	return (
		<aside
			data-ref="organism-sidebar"
			className={[
				/* BEM block marker (structural reference only) */
				'sidebar',
				/* Hidden on mobile, flex column on tablet+ */
				'hidden tablet:flex flex-col',
				/* Fixed positioning: pinned to left edge, full viewport height */
				'fixed top-0 left-0 h-screen',
				/* Width: 240px — the mockup called for 220px, but "Recurring Work
				   Orders" wraps to two lines at that width once the active item
				   turns semibold. 240px clears the longest label with the bold
				   weight applied. AppLayout mirrors this in its content offset. */
				'w-[240px]',
				/* White background with right border for separation from content */
				'bg-white border-r border-brand-border',
				/* Ensure sidebar sits above content but below modals */
				'z-40',
				/* Smooth transition for any responsive layout changes */
				'transition-all duration-200',
				/* Forwarded className last so callers can extend/override */
				className ?? '',
			].join(' ')}
		>
			{/* ===== Brand area (top) ===== */}
			{/* Displays the brand name and company tagline at the top of the sidebar */}
			<div className="sidebar__header flex flex-col items-center text-center px-20px py-24px border-b border-brand-border">
				{/* Brand block is centred (G Brands' mock-up): wordmark, tagline,
				    then the small "Powered by DIRT" credit underneath. */}
				{/* Brand name in heading font (Lulo One) — black letters, orange "G" */}
				<BrandWordmark as="h1" size="md" className="sidebar__brand" />
				{/* Company tagline — was a "WORK ORDERS" label, which read as if
				    the whole app were only work orders. */}
				<p className="sidebar__tagline font-sans text-12px text-brand-text-muted mt-4px mb-0">
					{BRAND_TAGLINE}
				</p>
				{/* Platform credit — the app is G Brands' DIRT install. Sits under
				    the customer brand, as in G Brands' mock-up. */}
				<PoweredByDirt className="sidebar__powered-by mt-16px" />
			</div>

			{/* ===== Main navigation section ===== */}
			{/* Core navigation links available to all authenticated users */}
			<nav className="sidebar__nav flex-1 py-16px overflow-y-auto" aria-label="Main navigation">
				{/* Main nav items: Dashboard, Work Orders, Inspections, Buildings */}
				<div className="space-y-2px">
					{mainNavItems.map(renderNavItem)}
				</div>

				{/* ===== Administration section (role-gated) ===== */}
				{/* Only rendered when the user has admin or developer role */}
				{adminNavItems.length > 0 && (
					<div className="mt-24px">
						{/* Section header — small uppercase label */}
						<p className="px-20px mb-8px text-10px font-sans font-semibold text-brand-text-subtle uppercase tracking-widest">
							Administration
						</p>
						{/* Admin nav items */}
						<div className="space-y-2px">
							{adminNavItems.map(renderNavItem)}
						</div>
					</div>
				)}
			</nav>

			{/* ===== Bottom utility section ===== */}
			{/* Pinned to the bottom of the sidebar via flex layout (mt-auto not needed
				because the nav section above uses flex-1 to consume remaining space) */}
			<div className="sidebar__footer border-t border-brand-border py-12px">
				{/* --- Sync Status item --- */}
				{/* Shows a colored dot (green = online, red = offline) next to a cloud icon */}
				<div className="flex items-center gap-12px px-20px py-8px text-14px font-sans text-brand-text-muted">
					{/* Cloud icon for sync status */}
					<svg className="w-20px h-20px flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z" />
					</svg>
					<span>Sync Status</span>
					{/* Online/offline indicator dot — green for online, red for offline */}
					<span
						className={[
							'w-8px h-8px rounded-full ml-auto flex-shrink-0',
							isOnline ? 'bg-status-success' : 'bg-status-error',
						].join(' ')}
						aria-label={isOnline ? 'Online' : 'Offline'}
					/>
				</div>

				{/* --- Settings/Profile link --- */}
				{/* Navigates to the user profile/settings page */}
				<a
					href="/profile"
					onClick={(e) => handleNavClick(e, '/profile')}
					className={[
						'flex items-center gap-12px px-20px py-8px min-h-44px desktop:min-h-0 text-14px font-sans transition-colors',
						isActive('/profile', location.pathname)
							? 'text-brand-primary font-semibold'
							: 'text-brand-text-muted hover:text-brand-primary',
					].join(' ')}
				>
					{/* Gear icon for settings */}
					<svg className="w-20px h-20px flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
						<path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
					</svg>
					<span>Settings</span>
				</a>
			</div>
		</aside>
	);
};

export default Sidebar;
